import React from 'react';
import './gallery-card.css';
import { Project } from '../../pages/Gallery';
import {
	IProjectShallowTransfer,
	ProjectState
} from '@elyx-code/project-logic-tree';
import { Box, LinearProgress, CircularProgress } from '@mui/material';
import {
	getAccessToken,
	getCurrentUserOrLogoutRedirect,
	getRefreshToken
} from '../../auth';
import { popupNotification } from '../../popup-notification';
import {
	DTOAction,
	ILiveMessageDTO,
	LiveMessageEventType
} from '@elyx-code/definitions';
import { Socket } from '../../socket/socket';
import { Logger } from '@elyx-code/common-ts-utils';
import { useWorkspaceStore } from '../../store/workspace';

import axios from 'axios';
import { envConfig } from '../../config';
import {
	saveCloningJob,
	removeCloningJob
} from '../../utils/cloningJobStorage';

interface ProjectDuplicationCardProps {
	project: Project;
	socket: Socket;
	onCloningComplete?: (newProject: Project) => void;
	onCloningFailed?: (error: any) => void;
	viewMode?: 'grid' | 'rows';
	jobId?: string;
	initialProgress?: number;
	initialLatestEvent?: string;
	isRestored?: boolean;
	restoredClonedProjectId?: string;
}

export enum ProjectCloningEvents {
	ProjectHeadLoaded = 'get-project-request-head',
	ProjectFetchProgress = 'get-project-request-progress',
	ProjectFetchComplete = 'get-project-request-complete',
	StartingToPersistEntities = 'starting-to-persist-entity-clones',
	NewProjectCloneCreated = 'new-project-clone-created',
	CloneProjectRequestComplete = 'clone-project-request-complete'
}

export class ProjectCloningRun {
	fetchActionId = ProjectState.UUID.uuid();
	lastChunkIndex: number | null = null;
	hasReceivedComplete = false;
	totalEntitiesToClone = 0;
	completedEntities = 0;

	resolve: (projectState: IProjectShallowTransfer) => void;

	onProjectHeadLoadedCallback: null | (() => void | Promise<void>) = null;
	onChunkAddedCallback: null | (() => void | Promise<void>) = null;
	onProjectFetchCompleteCallback: null | (() => void | Promise<void>) = null;
	onCloningCompleteCallback:
		| null
		| ((project: IProjectShallowTransfer) => void | Promise<void>) = null;
	onStartPersistingEntitiesCallback:
		| null
		| ((totalEntities: number) => void) = null;
	onEntityPersistedCallback:
		| null
		| ((data: { total: number; completed: number }) => void) = null;
	onCloningFailedCallback: null | ((error: any) => void) = null;

	constructor(
		public projectId: string,
		public websocketConnection: Socket,
		public workspaceId: string,
		public accessToken: string,
		public refreshToken: string
	) {}

	get pageSize() {
		return 10;
	}

	onProjectHeadLoaded(callback: () => void | Promise<void>) {
		this.onProjectHeadLoadedCallback = callback;
	}

	onChunkAdded(callback: () => void | Promise<void>) {
		this.onChunkAddedCallback = callback;
	}

	onProjectFetchComplete(callback: () => void | Promise<void>) {
		this.onProjectFetchCompleteCallback = callback;
	}

	onStartPersistingEntities(callback: (totalEntities: number) => void) {
		this.onStartPersistingEntitiesCallback = callback;
	}

	onEntityPersisted(
		callback: (data: { total: number; completed: number }) => void
	) {
		this.onEntityPersistedCallback = callback;
	}

	onCloningComplete(
		callback: (project: IProjectShallowTransfer) => void | Promise<void>
	) {
		this.onCloningCompleteCallback = callback;
	}

	onCloningFailed(callback: (error: any) => void) {
		this.onCloningFailedCallback = callback;
	}

	async clone(): Promise<IProjectShallowTransfer | null> {
		return new Promise(async (resolve, _reject) => {
			this.resolve = resolve;

			Logger.log('Cloning project', this.projectId);
			popupNotification({
				text: 'Copying project... This might take a few minutes depending on the size of the project'
			});

			const userId = (await getCurrentUserOrLogoutRedirect())?.id;

			// Subscribe to websocket channels
			this.websocketConnection.send({
				action: DTOAction.CloneProjectV1,
				actionId: this.fetchActionId,
				userAuthor: userId,
				agentAuthor: null,
				emittedAt: new Date().toISOString(),
				channel: `gallery--${userId}`,
				sessionId: null,
				authorization: 'Bearer ' + this.accessToken,
				refreshToken: this.refreshToken,
				workspaceId: this.workspaceId,
				type: LiveMessageEventType.Post,
				event: null,
				message: null,
				payload: {
					projectId: this.projectId
				}
			});

			this.websocketConnection.on(
				'message',
				(message: ILiveMessageDTO) => {
					if (
						this.hasReceivedComplete ||
						!message.actionId ||
						message.actionId !== this.fetchActionId ||
						!message.action ||
						message.action !== DTOAction.CloneProjectV1
					) {
						return;
					}

					Logger.log(
						'Project duplication run -- message received',
						message
					);
					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Progress &&
						message.event === 'get-project-request-head'
					) {
						Logger.log('Project head message received');
						this.onProjectHeadLoadedCallback?.();
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Progress &&
						message.event === 'get-project-request-progress'
					) {
						Logger.log(
							'Project cloning fetch progress message received'
						);
						this.onChunkAddedCallback();
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Progress &&
						message.event === 'get-project-request-complete'
					) {
						Logger.log('Project fetch completed message received');
						this.onProjectFetchCompleteCallback?.();
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Progress &&
						message.event === 'starting-to-persist-entity-clones'
					) {
						const data: {
							total: number;
							completed: number;
						} = message.payload as any;

						Logger.log('Persisting entities', data);
						this.totalEntitiesToClone = data.total;
						this.onStartPersistingEntitiesCallback?.(
							this.totalEntitiesToClone
						);
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Progress &&
						message.event === 'new-project-clone-created'
					) {
						const data: {
							total: number;
							completed: number;
						} = message.payload as any;

						Logger.log('Persisted new project', data);

						if (this.completedEntities >= data.completed) {
							return;
						} else {
							this.completedEntities = data.completed;
							this.onEntityPersistedCallback?.(data);
						}
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Progress &&
						message.event === 'starting-to-persist-entity-clones'
					) {
						const data: {
							total: number;
						} = message.payload as any;

						Logger.log('Starting to persist entity clones', data);

						this.totalEntitiesToClone = data.total;
						this.onStartPersistingEntitiesCallback?.(
							this.totalEntitiesToClone
						);
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Progress &&
						message.event === 'entity-cloned'
					) {
						const data: {
							total: number;
							completed: number;
						} = message.payload as any;

						Logger.log('SQL database table', data);

						if (this.completedEntities >= data.completed) {
							return;
						} else {
							this.completedEntities = data.completed;
							this.onEntityPersistedCallback?.(data);
						}
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Complete &&
						message.event === 'clone-project-request-complete'
					) {
						const data: IProjectShallowTransfer =
							message.payload as any;
						Logger.log(
							'Project cloning completed message received: ',
							data
						);
						this.hasReceivedComplete = true;
						this.onCloningCompleteCallback?.(data);
						popupNotification({
							text: 'Project copied successfully',
							color: 'primary'
						});
						this.resolve(data);
					}

					if (
						message.actionId === this.fetchActionId &&
						message.action === DTOAction.CloneProjectV1 &&
						message.type === LiveMessageEventType.Error
					) {
						Logger.log('Project cloning error message received');
						this.hasReceivedComplete = true;
						this.onCloningFailedCallback?.(null);
						popupNotification({
							text: 'Error copying project. Try again later',
							color: 'error'
						});
						this.resolve(null);
					}
				}
			);
		});
	}
}

function LinearDeterminate({ progress }: { progress: number }) {
	// const [progress, setProgress] = React.useState(0);

	// React.useEffect(() => {
	// 	const timer = setInterval(() => {
	// 		setProgress((oldProgress) => {
	// 			if (oldProgress === 100) {
	// 				return 0;
	// 			}
	// 			const diff = Math.random() * 10;
	// 			return Math.min(oldProgress + diff, 100);
	// 		});
	// 	}, 500);

	// 	return () => {
	// 		clearInterval(timer);
	// 	};
	// }, []);

	return (
		<Box sx={{ width: '100%' }}>
			<LinearProgress variant="determinate" value={progress} />
		</Box>
	);
}

export interface ILoadingCardProps {
	title?: string;
	subtitle?: string;
	description?: string;
	progress?: number;
	viewMode?: 'grid' | 'rows';
}

const ProjectCloningLoadingCard: React.FC<ILoadingCardProps> = (
	props: ILoadingCardProps
) => {
	const shouldShowProgress = props.progress !== undefined;

	if (props.viewMode === 'rows') {
		return (
			<div className="gallery-row-card gallery-row-card--cloning">
				<div className="gallery-row-card-left" style={{ flexGrow: 1 }}>
					<div className="gallery-row-card-image bg-color-5 loading">
						<CircularProgress
							size={20}
							variant={
								shouldShowProgress
									? 'determinate'
									: 'indeterminate'
							}
							value={props.progress}
						/>
					</div>
					<div
						className="gallery-row-card-info"
						style={{ flexGrow: 1 }}
					>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%'
							}}
						>
							<h3 className="gallery-row-card-title all-uppercase">
								{props.title || 'Duplicating'}
							</h3>
							<p
								className="gallery-row-card-description"
								style={{ margin: 0 }}
							>
								{props.subtitle || 'Duplicating project'}
							</p>
						</div>
						<p
							className="gallery-row-card-description"
							style={{ marginTop: '2px' }}
						>
							{props.description}
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="loading_card">
			<div className="loading_card__image"></div>
			{shouldShowProgress && (
				<LinearDeterminate progress={props.progress} />
			)}
			<div className="loading_card__content">
				<div
					style={{
						display: 'flex',
						width: '100%',
						gap: '8px',
						alignItems: 'center'
					}}
				>
					<h3
						className={`card-title ${
							!!props.title ? '' : ' loading'
						} all-uppercase`}
					>
						{props.title}
					</h3>
					<p
						style={{
							height: 'auto',
							margin: 0,
							fontSize: 10,
							fontWeight: 400,
							padding: 0,
							color: '#6c757d',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis'
						}}
					>
						{props.subtitle}
					</p>
				</div>
				<p
					className={`card-description${!!props.description ? '' : ' loading'}`}
					style={{ width: '100%' }}
				>
					{props.description}
				</p>
			</div>
		</div>
	);
};
const ProjectDuplicationCard: React.FC<ProjectDuplicationCardProps> = ({
	project,
	socket,
	onCloningComplete,
	onCloningFailed,
	viewMode = 'grid',
	jobId,
	initialProgress = 0,
	initialLatestEvent = 'Starting to clone project',
	isRestored = false,
	restoredClonedProjectId
}) => {
	const [progress, setProgress] = React.useState<number>(initialProgress);
	const [latestEvent, setLatestEvent] = React.useState<string>(initialLatestEvent);
	// This state is what the UI displays.
	const [displayEvent, setDisplayEvent] = React.useState<string>(initialLatestEvent);

	const activeJobIdRef = React.useRef<string>(jobId || project.id);
	const clonedProjectIdRef = React.useRef<string | undefined>(restoredClonedProjectId);

	// Ref to hold the progress value at the start of persisting entities
	const startPersistProgressRef = React.useRef<number>(0);
	const timeSinceProjectHeadLoaded = React.useRef<number>(0);

	// Ref to store the generic cycle timer IDs
	const genericCycleTimeoutRef = React.useRef<number | null>(null);

	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);

	const persistCurrentJob = React.useCallback(
		(currentProgress: number, currentEvent: string, clonedId?: string) => {
			if (!activeWorkspace?.id) return;
			getCurrentUserOrLogoutRedirect()
				.then((u) => {
					if (!u?.id) return;
					saveCloningJob({
						jobId: activeJobIdRef.current,
						sourceProjectId: project.id,
						sourceProject: project,
						clonedProjectId: clonedId || clonedProjectIdRef.current,
						workspaceId: activeWorkspace.id,
						userId: u.id,
						startedAt: Date.now(),
						progress: currentProgress,
						latestEvent: currentEvent,
						status: 'in_progress'
					});
				})
				.catch((err) => Logger.error('Failed to get user for cloning job save', err));
		},
		[activeWorkspace?.id, project]
	);

	const clearCurrentJob = React.useCallback(() => {
		if (!activeWorkspace?.id) return;
		getCurrentUserOrLogoutRedirect()
			.then((u) => {
				if (!u?.id) return;
				removeCloningJob(activeJobIdRef.current, activeWorkspace.id, u.id);
			})
			.catch((err) => Logger.error('Failed to clear cloning job', err));
	}, [activeWorkspace?.id]);

	// This function sets up a cycle of generic messages.
	const startGenericCycle = React.useCallback(() => {
		// Clear any previous timeout in case we're restarting the cycle.
		if (genericCycleTimeoutRef.current) {
			clearTimeout(genericCycleTimeoutRef.current);
		}

		// After 8 seconds, show "Please wait"
		genericCycleTimeoutRef.current = window.setTimeout(() => {
			setDisplayEvent('Please wait');

			// After another 8 seconds, show "Progress is still being made"
			genericCycleTimeoutRef.current = window.setTimeout(() => {
				setDisplayEvent('Progress is still being made');

				// After another 8 seconds, revert back to the latest event message and restart the cycle.
				genericCycleTimeoutRef.current = window.setTimeout(() => {
					setDisplayEvent(latestEvent);
					startGenericCycle();
				}, 8000);
			}, 8000);
		}, 8000);
	}, [latestEvent]);

	// Whenever latestEvent changes, reset displayEvent and restart the generic cycle.
	React.useEffect(() => {
		setDisplayEvent(latestEvent);
		startGenericCycle();

		return () => {
			if (genericCycleTimeoutRef.current) {
				clearTimeout(genericCycleTimeoutRef.current);
			}
		};
	}, [latestEvent, startGenericCycle]);

	const clone = async () => {
		const [accessToken, refreshToken] = await Promise.all([
			getAccessToken(),
			getRefreshToken()
		]);

		const projectCloningRun = new ProjectCloningRun(
			project.id,
			socket,
			activeWorkspace?.slug,
			accessToken,
			refreshToken
		);

		activeJobIdRef.current = projectCloningRun.fetchActionId;
		persistCurrentJob(0, 'Starting to clone project');

		projectCloningRun.onProjectHeadLoaded(() => {
			setProgress(5);
			setLatestEvent('Retrieving project data');
			timeSinceProjectHeadLoaded.current = Date.now();
			persistCurrentJob(5, 'Retrieving project data');
		});

		projectCloningRun.onChunkAdded(() => {
			setProgress((oldProgress) => {
				let newProgress = oldProgress;
				if (oldProgress >= 50) {
					newProgress = oldProgress;
				} else if (oldProgress >= 40) {
					newProgress = oldProgress + 0.5;
				} else if (timeSinceProjectHeadLoaded.current + 5000 < Date.now()) {
					newProgress = oldProgress + 1;
				} else if (timeSinceProjectHeadLoaded.current + 15000 < Date.now()) {
					newProgress = oldProgress + 0.5;
				} else {
					newProgress = oldProgress + 2;
				}
				persistCurrentJob(newProgress, latestEvent);
				return newProgress;
			});
		});

		projectCloningRun.onProjectFetchComplete(() => {
			setProgress((oldProgress) => {
				const newProgress = oldProgress < 50 ? oldProgress + 0.5 : oldProgress + 2;
				persistCurrentJob(newProgress, 'Project data retrieved');
				return newProgress;
			});
			setLatestEvent('Project data retrieved');
		});

		// When the entity cloning starts, capture the current progress.
		projectCloningRun.onStartPersistingEntities((totalEntities) => {
			Logger.log('Total entities to clone', totalEntities);
			const eventMsg = `Starting to clone ${totalEntities} entities`;
			setLatestEvent(eventMsg);
			// Capture the current progress
			setProgress((currentProgress) => {
				startPersistProgressRef.current = currentProgress;
				persistCurrentJob(currentProgress, eventMsg);
				return currentProgress;
			});
		});

		projectCloningRun.onEntityPersisted((data) => {
			Logger.log('Entity persisted', data);

			const eventMsg = `Cloning entry ${data.completed} of ${data.total}`;
			setLatestEvent(eventMsg);

			setProgress((oldProgress) => {
				if (oldProgress >= 95) {
					persistCurrentJob(oldProgress, eventMsg);
					return oldProgress;
				}

				const newProgress = Math.round(
					startPersistProgressRef.current +
						(data.completed / projectCloningRun.totalEntitiesToClone) *
							(95 - startPersistProgressRef.current)
				);
				Logger.log('New progress', newProgress);
				persistCurrentJob(newProgress, eventMsg);
				return newProgress;
			});
		});

		projectCloningRun.onCloningComplete(
			(projectResult: IProjectShallowTransfer) => {
				setProgress(100);
				clonedProjectIdRef.current = projectResult.id;
				clearCurrentJob();
				onCloningComplete?.({
					...projectResult,
					media: [],
					assignedFallbackColor: Math.floor(Math.random() * 5) + 1
				} as Project);
			}
		);

		projectCloningRun.onCloningFailed(() => {
			clearCurrentJob();
			onCloningFailed?.(null);
		});

		projectCloningRun.clone().then((projectState) => {
			Logger.log('Project state received', projectState);
			setProgress(100);
			clearCurrentJob();
		});
	};

	React.useEffect(() => {
		if (isRestored && restoredClonedProjectId) {
			let isCancelled = false;
			let attempts = 0;
			const maxAttempts = 60; // 2 minutes max (60 * 2s)

			const pollClonedProject = async () => {
				try {
					const token = await getAccessToken();
					const res = await axios.get(
						`${envConfig.API_BASE_URL}/editor-service/v1/project-meta/${restoredClonedProjectId}`,
						{
							headers: { Authorization: 'Bearer ' + token }
						}
					);

					if (isCancelled) return;

					if (res.status === 200 && res.data?.data?.project) {
						setProgress(100);
						setLatestEvent('Project copied successfully');
						clearCurrentJob();
						onCloningComplete?.({
							...res.data.data.project,
							media: [],
							assignedFallbackColor: Math.floor(Math.random() * 5) + 1
						} as Project);
						return;
					}
				} catch (err) {
					Logger.log('Polling restored clone project meta (still in progress or pending)', err);
				}

				attempts++;
				if (attempts >= maxAttempts) {
					if (!isCancelled) {
						clearCurrentJob();
						onCloningFailed?.('Cloning timed out after refresh');
					}
				} else if (!isCancelled) {
					setTimeout(pollClonedProject, 2000);
				}
			};

			pollClonedProject();

			return () => {
				isCancelled = true;
			};
		} else {
			clone();
		}
	}, [isRestored, restoredClonedProjectId]);

	return (
		<ProjectCloningLoadingCard
			progress={progress}
			title={`${progress}%`}
			subtitle="Duplicating project"
			description={displayEvent}
			viewMode={viewMode}
		/>
	);
};

export default ProjectDuplicationCard;
